import React, { useEffect, useState } from 'react';
import { StatCard } from '../UI';
import { fmt } from '../../utils';
import * as api from '../../api';

const iconProps = { width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };

const ClipboardIcon = () => (
  <svg {...iconProps}>
    <rect x="8" y="2" width="8" height="4" rx="1" />
    <path d="M9 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-3" />
    <path d="M9 12h6" /><path d="M9 16h6" />
  </svg>
);
const FilePlusIcon = () => (
  <svg {...iconProps}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
    <path d="M14 2v6h6" />
    <path d="M12 12v6" /><path d="M9 15h6" />
  </svg>
);
const FileTextIcon = () => (
  <svg {...iconProps}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
    <path d="M14 2v6h6" />
    <path d="M10 13H8" /><path d="M16 13h-2" /><path d="M10 17H8" /><path d="M16 17h-2" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg {...iconProps}>
    <circle cx="12" cy="12" r="10" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

export default function PVMSDashboard({
  onOpenLoadRequisition, onOpenNewVoucher, onOpenApproveVouchers, onOpenApprovalQueue,
  canApproveVouchers = false, canSeeApprovalQueue = false, toast,
}) {
  const [stats, setStats] = useState(null);
  const [hoveredKey, setHoveredKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [approvedReqs, posted, drafts, pending, dashboardStats] = await Promise.all([
          api.listApprovedRequisitionsForVoucher({ limit: 1000 }),
          api.listPaymentVouchers({ status: 'posted', limit: 1 }),
          api.listPaymentVouchers({ status: 'draft', limit: 1 }),
          api.listPaymentVouchers({ status: 'in_progress', limit: 1 }),
          api.getPaymentVoucherDashboardStats(),
        ]);
        if (cancelled) return;
        const outstanding = (approvedReqs?.requisitions || []).reduce((sum, r) => sum + Number(r.balance_amount || 0), 0);
        setStats({
          approved_available: approvedReqs?.total_count ?? 0,
          outstanding_amount: fmt(outstanding),
          posted_vouchers: posted?.total_count ?? 0,
          total_vat: fmt(dashboardStats?.total_vat ?? 0),
          total_wht: fmt(dashboardStats?.total_wht ?? 0),
          total_net_payable: fmt(dashboardStats?.total_net_payable ?? 0),
          draft_vouchers: drafts?.total_count ?? 0,
          in_progress: pending?.total_count ?? 0,
        });
      } catch (e) {
        toast.show(e.message, 'error');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const STATS = [
    { key: 'approved_available', label: 'Approved Reqs Available', color: 'blue', icon: '📋' },
    { key: 'outstanding_amount', label: 'Outstanding Req Amount', color: 'amber', icon: '₦' },
    { key: 'posted_vouchers', label: 'Posted Vouchers', color: 'green', icon: '✓' },
    { key: 'total_vat', label: 'Total VAT', color: 'amber', icon: '%' },
    { key: 'total_wht', label: 'Total WHT', color: 'red', icon: '%' },
    { key: 'total_net_payable', label: 'Total Net Payable', color: 'green', icon: '₦' },
    { key: 'draft_vouchers', label: 'Draft Vouchers', color: 'gray', icon: '📝' },
    { key: 'in_progress', label: 'In Progress', color: 'purple', icon: '⏳' },
  ];

  // Labels, titles and descriptions are taken verbatim from the reference
  // design — deliberately not paraphrased, so the wording stays identical.
  const WORKSPACES = [
    {
      key: 'load', Icon: ClipboardIcon, label: 'LOAD APPROVED REQUISITION', title: 'LOAD APP REQ', color: '#0b318b',
      desc: 'Load approved requisitions and process outstanding quantities with partial and multi-vendor support.',
      enabled: true, onClick: onOpenLoadRequisition,
    },
    {
      key: 'create', Icon: FilePlusIcon, label: 'NEW PAYMENT VOUCHER', title: 'CREATE PAYMENT VOUCHER', color: '#4338ca',
      desc: 'Create a new direct, reimbursement, statutory, advance, or other authorised payment voucher.',
      enabled: true, onClick: onOpenNewVoucher,
    },
    // Gated to the roles that can open the tab it leads to, mirroring
    // canSeeFinanceHeadQueueTab on the page.
    ...(canApproveVouchers ? [{
      key: 'approve', Icon: FileTextIcon, label: 'POSTED PAYMENT VOUCHERS', title: 'Vouchers Posted', color: '#16a34a',
      desc: 'View posted vouchers, deductions, net payable amounts and accounting details.',
      enabled: true, onClick: onOpenApproveVouchers,
    }] : []),
    // Final authorisation. Gated to md/dmd, mirroring canSeeApprovalQueue on
    // the page, so the card can never lead to a tab the user cannot open.
    ...(canSeeApprovalQueue ? [{
      key: 'approval-queue', Icon: CheckCircleIcon, label: 'APPROVED PAYMENT VOUCHERS', title: 'Voucher Review & Release', color: '#7c3aed',
      desc: 'Review posted vouchers, verify taxes/documents, process payments and release to the Expense Register.',
      enabled: true, onClick: onOpenApprovalQueue,
    }] : []),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
        {STATS.map((s) => (
          <StatCard key={s.key} label={s.label} value={stats ? stats[s.key] : undefined} color={s.color} icon={s.icon} style={{ border: '1px solid black', minHeight: 130, padding: '18px 20px' }} />
        ))}
      </div>

      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        {WORKSPACES.map((ws) => {
          const hovered = hoveredKey === ws.key;
          return (
            <div
              key={ws.key}
              onClick={ws.onClick}
              onMouseEnter={() => setHoveredKey(ws.key)}
              onMouseLeave={() => setHoveredKey(null)}
              style={{
                flex: '1 1 320px', minWidth: 300, minHeight: 190, cursor: 'pointer',
                border: hovered ? `2px solid ${ws.color}` : `1px solid ${ws.color}55`,
                borderRadius: 14, padding: 24,
                background: hovered ? `${ws.color}0f` : 'var(--bg-card)',
                boxShadow: hovered ? '0 8px 20px -6px rgba(0,0,0,0.18)' : 'var(--shadow-sm)',
                transform: hovered ? 'translateY(-3px)' : 'none',
                transition: 'all 0.18s ease', boxSizing: 'border-box',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 46, height: 46, borderRadius: 12, background: hovered ? ws.color : `${ws.color}22`, color: hovered ? '#fff' : ws.color, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.18s ease' }}>
                    <ws.Icon />
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', letterSpacing: 0.5 }}>{ws.label}</div>
                </div>
              </div>
              <div style={{ fontSize: 17, fontWeight: 800, color: ws.color, margin: '16px 0 6px', textTransform: 'uppercase' }}>{ws.title}</div>
              <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>{ws.desc}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
